import React from "react";
import styled, { keyframes } from "styled-components";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaInstagram } from "react-icons/fa";
import { Link } from "react-router-dom";

const GallerySection = () => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: false });

  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  // Gallery images - using available images
  const galleryItems = [
    { id: 1, image: "/images/img1.jpg" },
    { id: 2, image: "/images/img2.jpg" },
    { id: 3, image: "/images/img3.jpg" },
    { id: 4, image: "/images/335571427_155373320737332_967755093724042291_n.jpg" },
    { id: 5, image: "/images/bg-pic.jpg" },
    { id: 6, image: "/images/img1.jpg" },
  ];

  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <GalleryContainer id="gallery" ref={ref}>
      <BackgroundGlow />

      <Container>
        <SectionHeader>
          <motion.span
            className="subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
            }}
          >
            Latest Creations
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.2 },
              },
            }}
          >
            Our Gallery
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={controls}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: {
                opacity: 1,
                y: 0,
                transition: { duration: 0.6, delay: 0.4 },
              },
            }}
          >
            Explore our stunning collection of custom jewelry creations.
          </motion.p>
        </SectionHeader>

        <GalleryGrid
          variants={sectionVariants}
          initial="hidden"
          animate={controls}
        >
          {galleryItems.map((item) => (
            <GalleryItem
              key={item.id}
              variants={itemVariants}
              whileHover={{ y: -10, scale: 1.02 }}
            >
              <InstagramIcon
                href="https://www.instagram.com/bmoregrillz?igsh=MXNhb3dzNWVjeHNoMw=="
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaInstagram />
              </InstagramIcon>
              <GalleryImage src={item.image} alt="Gallery item" />
            </GalleryItem>
          ))}
        </GalleryGrid>

        <ButtonContainer
          initial={{ opacity: 0, y: 30 }}
          animate={controls}
          variants={{
            hidden: { opacity: 0, y: 30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.6, delay: 0.8 },
            },
          }}
        >
          <ViewAllButton
            to="/gallery"
            whileHover={{
              y: -5,
              boxShadow: "0 10px 25px rgba(255, 215, 0, 0.3)",
            }}
          >
            View Full Gallery
          </ViewAllButton>
        </ButtonContainer>
      </Container>
    </GalleryContainer>
  );
};

// Keyframes for card glow
const cardGlow = keyframes`
  0%, 100% {
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  }
  50% {
    box-shadow: 0 20px 45px rgba(0, 0, 0, 0.4), 0 0 30px rgba(255, 215, 0, 0.1);
  }
`;

// Styled Components
const GalleryContainer = styled.section`
  background-color: var(--dark-bg);
  position: relative;
  overflow: hidden;
  padding: var(--spacing-xl) 0;
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0.05;
  background: linear-gradient(135deg, var(--gold) 0%, transparent 70%);
  z-index: 0;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-lg);

  .subtitle {
    color: var(--gold);
    text-transform: uppercase;
    letter-spacing: 4px;
    font-size: 0.9rem;
    margin-bottom: var(--spacing-xs);
    display: block;
  }

  h2 {
    font-size: 3rem;
    background: var(--gold-gradient);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: var(--spacing-sm);
    position: relative;
    display: inline-block;
    text-transform: uppercase;
    font-weight: 800;

    &::after {
      content: "";
      position: absolute;
      bottom: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background: var(--gold-gradient);
      border-radius: 2px;
    }

    @media (max-width: 768px) {
      font-size: 2.5rem;
    }

    @media (max-width: 576px) {
      font-size: 2rem;
    }
  }

  p {
    max-width: 600px;
    margin: var(--spacing-md) auto 0;
    font-size: 1.1rem;
    color: #d0d0d0;
    line-height: 1.8;

    @media (max-width: 576px) {
      font-size: 1rem;
    }
  }
`;

const GalleryGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-md);

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const GalleryItem = styled(motion.div)`
  position: relative;
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.3);
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  aspect-ratio: 1/1;
  cursor: pointer;
  border: 1px solid rgba(255, 215, 0, 0.05);

  &:hover {
    border-color: rgba(255, 215, 0, 0.3);
    animation: ${cardGlow} 2s ease-in-out infinite;
  }
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  ${GalleryItem}:hover & {
    transform: scale(1.1);
    filter: brightness(1.1) saturate(1.1);
  }
`;

const InstagramIcon = styled.a`
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  text-decoration: none;

  svg {
    color: white;
    font-size: 18px;
    transition: all 0.3s ease;
  }

  &:hover {
    background: var(--gold-gradient);
    border-color: transparent;
    transform: scale(1.1) rotate(-5deg);

    svg {
      color: var(--black);
    }
  }
`;

const ButtonContainer = styled(motion.div)`
  text-align: center;
  margin-top: var(--spacing-lg);
`;

const ViewAllButton = styled(motion(Link))`
  display: inline-block;
  padding: 16px 40px;
  background: transparent;
  color: var(--gold);
  border: 1px solid var(--gold);
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background: var(--gold-gradient);
    color: var(--black);
    border-color: transparent;
  }

  @media (max-width: 576px) {
    padding: 14px 30px;
    font-size: 0.9rem;
    width: 100%;
  }
`;

export default GallerySection;
